import React, { useState, useEffect } from 'react';
import './ViewDrawer.css';
import BodyLargeMedium from '../designSystem/BodyLargeMedium';
import closeIcon from "../../assets/images/closeIcon.svg";
import EventCard from './EventCard';
import BodySmallMedium from '../designSystem/BodySmallMedium';
import visitProfileIcon from "../../assets/images/visitprofile.svg";
import { useSelector } from 'react-redux';

const ViewDrawer = ({ isOpen, onClose, day, events }) => {
    const [activeVisitProfileId, setActiveVisitProfileId] = useState(null);
    const userDetail = useSelector((state) => state.employees_reducer.employee);

    const formattedDate = day
        ? new Intl.DateTimeFormat('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).format(new Date(day.year, day.month - 1, day.day))
        : '';

    // Check if events has NEW visit profiles structure (visitProfiles array)
    const hasNewVisitProfilesStructure = () => {
        if (!events || !Array.isArray(events)) return false;
        return events.some(e => e.visitProfiles && e.visitProfiles.length > 0);
    };

    // Check if events has OLD visit profile structure (visitProfile object)
    const hasOldVisitProfileStructure = () => {
        if (!events || !Array.isArray(events)) return false;
        return events.some(e => e.visitProfile && e.visitProfile.visitProfileId);
    };

    const hasNewStructure = hasNewVisitProfilesStructure();
    const hasOldStructure = hasOldVisitProfileStructure();
    const hasAnyVisitProfiles = hasNewStructure || hasOldStructure;

    // Get all visit profiles with their names
const getVisitProfiles = () => {
    if (!events || !Array.isArray(events)) return [];
    
    const visitProfileIdsSet = new Set();
    
    // Collect visit profile IDs from BOTH structures
    events.forEach(event => {
        // NEW structure
        if (event.visitProfiles && event.visitProfiles.length > 0) {
            event.visitProfiles.forEach(vp => {
                visitProfileIdsSet.add(vp.visitProfileId);
            });
        }
        // OLD structure
        if (event.visitProfile && event.visitProfile.visitProfileId) {
            visitProfileIdsSet.add(event.visitProfile.visitProfileId);
        }
    });
    
    const visitProfileIds = Array.from(visitProfileIdsSet);
    
    // Try to match with userDetail to get proper names
    if (userDetail && userDetail !== "isLoading" && userDetail.visitProfiles) {
        const matchedProfiles = userDetail.visitProfiles
            .filter(profile => visitProfileIds.includes(profile.visitProfileId))
            .map(profile => ({
                visitProfileId: profile.visitProfileId,
                visitProfileName: profile.profileName
            }));
        
        if (matchedProfiles.length > 0) {
            return matchedProfiles;
        }
    }
    
    // Fallback: If no userDetail or no matches, use numbered names
    return visitProfileIds.map((id, index) => ({
        visitProfileId: id,
        visitProfileName: `Visit Profile ${index + 1}`
    }));
};

    const visitProfiles = getVisitProfiles();

    // Set initial active visit profile
    useEffect(() => {
        if (isOpen && hasAnyVisitProfiles && visitProfiles.length > 0) {
            setActiveVisitProfileId(visitProfiles[0].visitProfileId);
        } else {
            setActiveVisitProfileId(null);
        }
    }, [isOpen, events, hasAnyVisitProfiles, visitProfiles.length]);

    // Get plan details for active visit profile or direct plan details
    const getActivePlanDetails = () => {
        if (!events || !Array.isArray(events)) return [];

        if (hasAnyVisitProfiles && activeVisitProfileId) {
            const planDetails = [];
            
            events.forEach(event => {
                // NEW structure - check visitProfiles array
                if (event.visitProfiles && event.visitProfiles.length > 0) {
                    const matchingProfile = event.visitProfiles.find(
                        vp => vp.visitProfileId === activeVisitProfileId
                    );
                    
                    if (matchingProfile && matchingProfile.planDetails) {
                        planDetails.push(...matchingProfile.planDetails);
                    }
                }
                
                // OLD structure - check visitProfile object and planDetails
                if (event.visitProfile && 
                    event.visitProfile.visitProfileId === activeVisitProfileId &&
                    event.planDetails && 
                    event.planDetails.length > 0) {
                    planDetails.push(...event.planDetails);
                }
            });
            
            return planDetails;
        }

        // No visit profiles - collect direct planDetails from all events
        const planDetails = [];
        events.forEach(event => {
            if (event.planDetails && event.planDetails.length > 0) {
                planDetails.push(...event.planDetails);
            }
        });
        
        return planDetails;
    };

    const activePlanDetails = getActivePlanDetails();

    // Check if on leave
    const isOnLeave = events && Array.isArray(events) && events.some(e => e.onLeave);

    // Debug logging
    console.log('ViewDrawer Debug:', {
        events,
        hasNewStructure,
        hasOldStructure,
        hasAnyVisitProfiles,
        visitProfiles,
        activeVisitProfileId,
        activePlanDetails
    });

    return (
        <>
            <div
                className={`view-drawer-backdrop ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            ></div>

            <div className={`view-drawer-container ${isOpen ? 'open' : ''}`}>
                <div className="view-drawer-header">
                    <div className="header-content">
                        <BodyLargeMedium text={formattedDate} color='#384153' />
                        {isOnLeave && (
                            <span className="on-leave-badge">On Leave</span>
                        )}
                    </div>
                    <button className='close-icon' onClick={onClose}>
                        <img src={closeIcon} alt="close" />
                    </button>
                </div>

                <div className='view-drawer-main'>
                    {/* Visit Profile Tabs - Show if ANY visit profiles exist */}
                    {hasAnyVisitProfiles && visitProfiles.length > 0 && (
                        <div className="visit-profile-tabs-container">
                            <div className="visit-profile-label">
                                <img src={visitProfileIcon} alt="visit profile icon" />
                                <BodySmallMedium text="Visit Profiles" color='#1E293B' />
                            </div>
                            <div className="tabs-header">
                                {visitProfiles.map((profile) => (
                                    <button
                                        key={profile.visitProfileId}
                                        className={`tab-button ${activeVisitProfileId === profile.visitProfileId ? 'active' : ''}`}
                                        onClick={() => setActiveVisitProfileId(profile.visitProfileId)}
                                    >
                                        {profile.visitProfileName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Plan Details */}
                    {isOpen && activePlanDetails && activePlanDetails.length > 0 ? (
                        activePlanDetails.map((plan, index) => (
                            <EventCard 
                                key={plan.id || index} 
                                event={plan} 
                                planCityName={plan.cityName} 
                            />
                        ))
                    ) : (
                        !isOnLeave && (
                            <div className="empty-state">
                                <p>No plans available for this day</p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    );
};

export default ViewDrawer;